import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { posApiFetch } from "@/utils/pos-api";

// POS role types mirroring the POS admin system
export const PosUserRole = {
  ADMIN: "admin" as const,
  MANAGER: "manager" as const,
  STAFF: "staff" as const,
} as const;

export type PosUserRoleType = (typeof PosUserRole)[keyof typeof PosUserRole];

// Minimal POS user shape used by admin components
export interface PosUser {
  id: number;
  email: string;
  displayName?: string | null;
  role: PosUserRoleType;
  status: string;
  imageUrl?: string | null;
  originalImageUrl?: string | null;
  lastLoginAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

const STORAGE_KEY = "sech_pos_admin";

interface StoredSession {
  user: PosUser;
  timestamp: number;
}

interface PosAuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  user: PosUser | null;
  hasRole: (role: PosUserRoleType) => boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const PosAuthContext = createContext<PosAuthContextType | null>(null);

function getStoredSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as StoredSession;
    if (!data.user?.id || !data.user?.email || !data.user?.role) return null;
    return data;
  } catch {
    return null;
  }
}

function storeSession(user: PosUser): void {
  const session: StoredSession = { user, timestamp: Date.now() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Independent POS admin auth provider.
 * Authenticates against /v1/pos/auth/login, manages session in localStorage.
 * Completely independent of Firebase.
 */
export function PosAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PosUser | null>(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = !!user;

  // Restore session on mount
  useEffect(() => {
    const session = getStoredSession();
    if (session) {
      setUser(session.user);
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const res = await posApiFetch("/v1/pos/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Login failed" }));
        return { success: false, message: err.message || "Invalid credentials" };
      }

      const userData = await res.json();

      // Backend returns user object flat (no { user: ... } wrapper)
      const posUser: PosUser = {
        id: userData.id,
        email: userData.email,
        displayName: userData.displayName || userData.name || null,
        role: userData.role,
        status: userData.status,
        imageUrl: userData.imageUrl || null,
        originalImageUrl: userData.originalImageUrl || null,
        lastLoginAt: userData.lastLoginAt || null,
        createdAt: userData.createdAt,
        updatedAt: userData.updatedAt,
      };

      storeSession(posUser);
      setUser(posUser);

      return { success: true };
    } catch (err) {
      console.error("POS login error:", err);
      return { success: false, message: "Login failed. Please try again." };
    }
  }, []);

  const logout = useCallback(async () => {
    // Notify server
    try {
      await posApiFetch("/v1/pos/auth/logout", { method: "POST" });
    } catch {
      // ignore — clear session regardless
    }
    clearSession();
    setUser(null);
  }, []);

  const hasRole = useCallback(
    (role: PosUserRoleType): boolean => {
      if (!user) return false;
      if (user.role === PosUserRole.ADMIN) return true;
      return user.role === role;
    },
    [user]
  );

  const refreshUser = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await posApiFetch(`/v1/pos/users/${user.id}`);
      if (res.ok) {
        const userData = await res.json();
        const updated: PosUser = {
          ...user,
          ...userData,
          displayName: userData.displayName || userData.name || user.displayName,
          imageUrl: userData.imageUrl || user.imageUrl,
        };
        storeSession(updated);
        setUser(updated);
      }
    } catch {
      // ignore
    }
  }, [user]);

  return (
    <PosAuthContext.Provider
      value={{ isAuthenticated, loading, user, hasRole, login, logout, refreshUser }}
    >
      {children}
    </PosAuthContext.Provider>
  );
}

export function usePosAuth() {
  const ctx = useContext(PosAuthContext);
  if (!ctx) {
    throw new Error("usePosAuth must be used inside PosAuthProvider");
  }
  return ctx;
}
